import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';
import type { ThemePreference } from '@mg/shared';
import { useAuth } from '../../auth/AuthContext';
import { api } from '../../lib/api';
import { useTheme } from '../../theme/ThemeContext';
import { THEME_SAVE_KEY } from '../../theme/themeSave';

/** The one line under the tiles when a save fails. By then the theme has already gone back. */
export const THEME_SAVE_FAILED = 'Could not save your theme. Try again.';

const OPTIONS: ReadonlyArray<{ value: ThemePreference; label: string; helper: string; icon: LucideIcon }> = [
  { value: 'LIGHT', label: 'Light', helper: 'Always light', icon: Sun },
  { value: 'DARK', label: 'Dark', helper: 'Always dark', icon: Moon },
  { value: 'SYSTEM', label: 'System', helper: 'Match your OS', icon: Monitor },
];

/** One save: the choice, its place in the order the user made the choices, and whose choice it is. */
interface ThemeSave {
  preference: ThemePreference;
  seq: number;
  userId: string;
}

/**
 * Per user, shared by every Appearance card: the newest choice's number, and what the server holds
 * (where a failed save goes back to). Not kept in the card, because a save outlives the card that
 * started it: leave My Profile and come back, or let an auth refresh() remount the page, while a save
 * is on its way, and its answer still arrives through the first card's callbacks. A card of its own
 * would count from 0 again and start from a principal read before that save arrived, so it could
 * revert to a value the server no longer holds, or let the older save undo a newer choice.
 */
const savesByUser = new Map<string, { latest: number; confirmed: ThemePreference }>();

function savesFor(userId: string, held: ThemePreference) {
  let saves = savesByUser.get(userId);
  if (!saves) {
    saves = { latest: 0, confirmed: held };
    savesByUser.set(userId, saves);
  }
  return saves;
}

/**
 * My Profile > Appearance: Light, Dark or System.
 *
 * A choice applies at once (ThemeProvider: the `dark` class and `color-scheme` on <html>, plus this
 * browser's copy) and is then saved for the user, so it follows them to other devices. There is no
 * Save button and no "Saved" note. The chip says what is on screen (System resolves through the OS),
 * not what was picked.
 *
 * A failed save puts the theme back to the value the server holds and says so in one line, so this
 * browser never quietly disagrees with the user's stored choice. The arrow keys select, so arrowing
 * across the tiles sends a burst of saves: they run one at a time in the order chosen (the mutation
 * scope), and only the newest choice's failure reverts anything.
 *
 * The keyboard handling is RadioRows' (features/design/RadioRows.tsx): a roving tabindex, arrows that
 * select and wrap, focus moving with the selection, and Space or Enter through the native button.
 */
export function AppearanceCard() {
  const titleId = useId();
  const { principal, updateUser } = useAuth();
  const { preference, resolved, setPreference } = useTheme();
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState<string | null>(null);
  const tiles = useRef(new Map<ThemePreference, HTMLButtonElement>());
  const userId = principal?.user.id ?? '';
  const held = principal?.user.themePreference ?? preference;

  // Where a failed save goes back to. With no save on its way, that is the principal: each save's
  // answer folds into it, and an auth refresh() reads it again. While one is on its way, only the
  // answers move it on (onSuccess), because the principal may have been read before that save arrived.
  useEffect(() => {
    const saves = savesFor(userId, held);
    if (queryClient.isMutating({ mutationKey: THEME_SAVE_KEY }) === 0) saves.confirmed = held;
  }, [userId, held, queryClient]);

  const save = useMutation({
    // ThemeServerSync looks for this key: while a save is on its way, it keeps the choice on screen.
    mutationKey: THEME_SAVE_KEY,
    // One PUT at a time, in the order chosen, so the last choice is the one the server keeps.
    scope: { id: 'theme-preference' },
    mutationFn: ({ preference: next }: ThemeSave) => api.updateMeSettings({ themePreference: next }),
    onSuccess: (user, saved) => {
      savesFor(saved.userId, user.themePreference).confirmed = user.themePreference;
      updateUser(user);
    },
    onError: (_error, failed) => {
      const saves = savesByUser.get(failed.userId);
      // A newer choice is queued behind this one, from this card or one opened since: that one decides.
      if (!saves || failed.seq !== saves.latest) return;
      // The server already holds this choice, so nothing was lost.
      if (failed.preference === saves.confirmed) return;
      setPreference(saves.confirmed);
      setSaveError(THEME_SAVE_FAILED);
    },
  });

  const choose = (next: ThemePreference) => {
    if (next === preference) return;
    // The newest choice, counted rather than compared by value: Light, Dark, Light, Dark sends Dark
    // twice, and the first Dark failing must not undo anything while the second is still on its way.
    const saves = savesFor(userId, held);
    saves.latest += 1;
    setSaveError(null);
    setPreference(next);
    save.mutate({ preference: next, seq: saves.latest, userId });
  };

  const move = (delta: number) => {
    const at = OPTIONS.findIndex((o) => o.value === preference);
    // Wraps, like a native group: from the last tile, Right goes back to the first.
    const next = OPTIONS[(at + delta + OPTIONS.length) % OPTIONS.length];
    if (!next) return;
    choose(next.value);
    // Focus goes with the selection. Left on the old tile, the next Space or Enter would press that
    // tile and put the previous choice back.
    tiles.current.get(next.value)?.focus();
  };

  return (
    <section aria-labelledby={titleId} className="rounded-2xl border border-line bg-card p-5 shadow-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 id={titleId} className="text-[0.9375rem] font-semibold text-ink">
            Appearance
          </h3>
          <p className="mt-0.5 text-sm text-ink-muted">Choose your theme. System follows your operating system.</p>
        </div>
        {/* One text node, so a change is read as "Currently dark", not only as the word that changed. */}
        <span
          aria-live="polite"
          className="inline-flex h-7 shrink-0 items-center rounded-full border border-line bg-chip px-3 text-[0.8125rem] font-medium text-ink-strong"
        >
          {`Currently ${resolved}`}
        </span>
      </div>

      <div role="radiogroup" aria-label="Theme" className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {OPTIONS.map(({ value, label, helper, icon: Icon }) => {
          const checked = value === preference;
          return (
            <button
              key={value}
              ref={(el) => {
                if (el) tiles.current.set(value, el);
                else tiles.current.delete(value);
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              // Only the selected tile is tabbable, so Tab enters the group once and leaves it once.
              tabIndex={checked ? 0 : -1}
              onClick={() => choose(value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                  e.preventDefault();
                  move(1);
                } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  move(-1);
                }
              }}
              // Selected: a 2px action border and the tint fill. The padding drops by the extra pixel
              // of border, so the tile's content does not shift when it is selected: p-4 less 1px,
              // with the pixel in px like the border, so it holds at every scale.
              className="focus-ring group flex min-h-[7.5rem] flex-col items-start gap-3 rounded-xl border border-line bg-card p-4 text-left aria-checked:border-2 aria-checked:border-action aria-checked:bg-tint aria-checked:p-[calc(1rem-1px)]"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-chip text-ink-muted group-aria-checked:bg-action group-aria-checked:text-action-fg">
                <Icon className="h-5 w-5" strokeWidth={1.8} aria-hidden />
              </span>
              <span>
                <span className="block text-[0.9375rem] font-semibold text-ink">{label}</span>
                <span className="block text-[0.84375rem] text-ink-muted">{helper}</span>
              </span>
            </button>
          );
        })}
      </div>

      {saveError && (
        <p role="alert" className="mt-3 text-sm text-red-600 dark:text-error-dark">
          {saveError}
        </p>
      )}
    </section>
  );
}
